import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "@/lib/utils";
import { motion } from "framer-motion";
import { Car, MapPin, ArrowRight, CheckCircle2, Smartphone, AlertTriangle, Info, CreditCard, Loader2, Navigation, LocateFixed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import MapLocationPicker from "@/components/MapLocationPicker";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";



const TowingBookingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState("new");
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_phone: user.phone || prev.customer_phone,
        customer_email: user.email || prev.customer_email,
      }));
    }
  }, [user]);

  const [formData, setFormData] = useState({
    pickup_address: "",
    dropoff_address: "",
    vehicle_type: "",
    vehicle_make_model: "",
    towing_reason: "",
    is_drivable: false,
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    notes: "",
    pickup_coordinates: null
  });

  const vehicleTypes = [
    { value: "motorcycle", label: "Motorcycle" },
    { value: "sedan", label: "Sedan / Compact" },
    { value: "suv", label: "SUV / Crossover" },
    { value: "truck", label: "Pickup / Truck" }
  ];

  const towingReasons = [
    { value: "breakdown", label: "Breakdown / Won't Start" },
    { value: "accident", label: "Accident / Collision" },
    { value: "relocation", label: "Vehicle Relocation" },
    { value: "other", label: "Other" }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const useMyLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setLocationLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const coords = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        
        try {
          // Try to reverse geocode using a free service
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
          );
          const data = await response.json();
          
          if (data.display_name) {
            setFormData(prev => ({
              ...prev,
              pickup_address: data.display_name,
              pickup_coordinates: coords
            }));
            toast.success("Location detected successfully!");
          } else {
            // Fallback to coordinates if address not found
            setFormData(prev => ({
              ...prev,
              pickup_address: `GPS: ${coords}`,
              pickup_coordinates: coords
            }));
            toast.success("GPS coordinates captured!");
          }
        } catch (error) {
          // Fallback to coordinates if reverse geocoding fails
          setFormData(prev => ({
            ...prev,
            pickup_address: `GPS: ${coords}`,
            pickup_coordinates: coords
          }));
          toast.success("GPS coordinates captured!");
        }
        
        setLocationLoading(false);
      },
      (error) => {
        setLocationLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Please allow location access to use this feature");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information is unavailable");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out");
            break;
          default:
            toast.error("Unable to get your location");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const getEstimate = async () => {
    if (!formData.pickup_address || !formData.dropoff_address || !formData.vehicle_type || !formData.towing_reason) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!pickupCoords) {
      toast.error("Please select a valid pickup location on the map");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/towing/estimate`, { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude });
      setEstimate(res.data);
      setStep(2);
    } catch {
      toast.error("Failed to get estimate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitBooking = async () => {
    if (!formData.customer_name || !formData.customer_phone || !formData.customer_email || !formData.vehicle_make_model) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/towing/booking`, { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude });
      setStep(3);
      toast.success("Towing request submitted!");
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const proceedToPayment = async () => {
    if (!formData.vehicle_make_model) {
      toast.error("Please enter your vehicle make & model");
      return;
    }

    setPaymentLoading(true);
    try {
      const bookingResponse = await axios.post(`${API}/towing/booking`, { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude });
      const booking = bookingResponse.data;
      const serviceData = {
        booking_id: booking.id,
        booking_type: "towing",
        amount: estimate.max_price,
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        service_type: "towing",
        pickup_address: formData.pickup_address,
        dropoff_address: formData.dropoff_address,
        pickup_coords: pickupCoords,
        dropoff_coords: dropoffCoords,
      };

      if (selectedCardId && selectedCardId !== "new") {
        const authResponse = await axios.post(`${API}/payments/authorize-hold`, {
          ...serviceData,
          payment_method_id: selectedCardId,
        }, { withCredentials: true });
        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your driver...");
          setStep(3);
        } else {
          toast.error("Payment authorization failed. Please try again.");
        }
        setPaymentLoading(false);
      } else {
        const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
          ...serviceData,
          origin_url: window.location.origin,
        });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      console.error("Authorization error:", error);
      toast.error(getErrorMessage(error, "Payment authorization failed. Please try another payment method."));
      setPaymentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="towing-booking-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_stackcart-app/artifacts/irbjz1yp_20200923_135235-scaled.jpg')" }}
        ></div>
        {/* Dark Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-900/90 via-orange-800/85 to-orange-900/80"></div>
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <Car className="w-8 h-8 text-orange-500" />
            </div>
            <h1 className="heading-2 text-white mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>Towing Services</h1>
            <p className="text-lg md:text-xl leading-relaxed text-white" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
              24/7 vehicle towing for breakdowns, accidents, or relocations. Our network of tow truck operators is ready to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-container py-12">
        <div className="max-w-3xl mx-auto">
          {/* Towing Details */}
          {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8" data-testid="towing-step-1">
              <h2 className="heading-3 text-emerald-950 mb-6">Towing Details</h2>

              <div className="space-y-6">
                <MapLocationPicker
                  pickupAddress={formData.pickup_address}
                  dropoffAddress={formData.dropoff_address}
                  onPickupChange={(val) => handleInputChange("pickup_address", val)}
                  onDropoffChange={(val) => handleInputChange("dropoff_address", val)}
                  onPickupCoordsChange={setPickupCoords}
                  onDropoffCoordsChange={setDropoffCoords}
                  pickupLabel="Pickup Location *"
                  dropoffLabel="Drop-off Location *"
                  pickupPlaceholder="Where is your vehicle?"
                  dropoffPlaceholder="Where should we tow it?"
                  serviceType="towing"
                />

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-emerald-950 font-medium mb-2 block">Vehicle Type *</Label>
                    <Select value={formData.vehicle_type} onValueChange={(value) => handleInputChange("vehicle_type", value)}>
                      <SelectTrigger className="input-style" data-testid="vehicle-type-select">
                        <SelectValue placeholder="Select vehicle type" />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicleTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-emerald-950 font-medium mb-2 block">Reason for Towing *</Label>
                    <Select value={formData.towing_reason} onValueChange={(value) => handleInputChange("towing_reason", value)}>
                      <SelectTrigger className="input-style" data-testid="towing-reason-select">
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {towingReasons.map((reason) => (
                          <SelectItem key={reason.value} value={reason.value}>{reason.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl">
                  <Checkbox id="drivable" checked={formData.is_drivable} onCheckedChange={(checked) => handleInputChange("is_drivable", checked)} data-testid="is-drivable-checkbox" />
                  <label htmlFor="drivable" className="text-stone-700">Vehicle can still be driven (wheels turn freely)</label>
                </div>

                {!formData.is_drivable && (
                  <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800">Non-drivable vehicles require flatbed towing.</p>
                  </div>
                )}

                <div>
                  <Label className="text-emerald-950 font-medium mb-2 block">Vehicle Make & Model *</Label>
                  <Input
                    className="input-style"
                    placeholder="e.g. Toyota Camry 2022"
                    value={formData.vehicle_make_model}
                    onChange={(e) => handleInputChange("vehicle_make_model", e.target.value)}
                    data-testid="vehicle-make-model-input"
                  />
                </div>

                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full py-6 font-bold" onClick={getEstimate} disabled={loading} data-testid="get-estimate-btn">
                  {loading ? "Getting Estimate..." : "Continue"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
            )}

            {step === 2 && estimate && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 mt-6" data-testid="payment-step">
                <h2 className="text-xl font-bold text-emerald-950 mb-4">Payment</h2>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-stone-600">Estimated Price</p>
                  <p className="text-3xl font-bold text-orange-600">${estimate.max_price.toFixed(2)} <span className="text-sm font-normal text-stone-500">CAD</span></p>
                </div>
                <SavedCardsSelector onSelect={setSelectedCardId} selectedCardId={selectedCardId} />
                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full py-6 font-bold mt-4" onClick={proceedToPayment} disabled={paymentLoading} data-testid="pay-btn">
                  {paymentLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Pay & Find Driver <ArrowRight className="w-5 h-5 ml-2" /></>}
                </Button>
              </motion.div>
            )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TowingBookingPage;
